// covered-by: frontends/carrie/src/components/ask-puntt-thread/__tests__/AskPunttThread.test.tsx
import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { type ReactNode, useMemo, useRef } from "react";

import {
  isEmptyAssistantPlaceholder,
  shouldHideEmptyAssistantPlaceholder,
} from "./threadVisibility";

import {
  isFeedMessageFromViewer,
  isHiddenMessage,
  resolveFeedActorForUserMessage,
} from "../../routes/ask-puntt/chatUtils";
import { ChatMessage } from "../../routes/ask-puntt/components/ChatMessage";
import { DayDivider } from "../../routes/ask-puntt/components/DayDivider";
import { FeedCompactLayoutProvider } from "../../routes/ask-puntt/components/FeedCompactLayoutContext";
import { PunttAvatar } from "../../routes/ask-puntt/components/PunttAvatar";
import { ThinkingState } from "../../routes/ask-puntt/components/ThinkingState";
import { useContainerWidth } from "../../routes/ask-puntt/hooks/useContainerWidth";
import {
  FEED_COMPACT_AUTHOR_MAX_WIDTH_PX,
  feedMessageAuthorKey,
  isFeedAuthorGroupStart,
} from "../../routes/ask-puntt/utils/messageAuthorGrouping";
import {
  calendarDayKey,
  formatDayDivider,
} from "../../routes/ask-puntt/utils/messageDateGrouping";
import { AVATAR_SIZE_BIG } from "../AvatarWithInitials";

export type AskPunttThreadPresentation = "chat" | "feed";

export type AskPunttThreadFeedMember = {
  _id: string;
  name?: string;
  email?: string;
  avatar?: string | null;
};

export type AskPunttThreadProps = {
  messages: AskPunttMessage[];
  presentation?: AskPunttThreadPresentation;
  chatId?: string;
  waitingForAI?: boolean;
  feedActorName?: string;
  feedActorAvatar?: string | null;
  feedMembers?: AskPunttThreadFeedMember[];
  currentUserId?: string;
  chatOwnerId?: string;
  renderAfterMessage?: (message: AskPunttMessage) => ReactNode;
  renderBeforeMessage?: (message: AskPunttMessage) => ReactNode;
  /** Suppress assistant thinking chrome (e.g. aborted V2 transport rows). */
  hideAgentChromeForMessage?: (message: AskPunttMessage) => boolean;
  /**
   * Sends the person's next message, in words. When set, a form the agent
   * drew on the LAST row (`message.components`) takes an answer; every
   * earlier form is read-only history. Unset, every form is read-only.
   */
  onSubmitAnswers?: (formattedAnswers: string) => void;
  /**
   * Locks a form's controls while a turn is in flight. Must be the SAME signal
   * `send()` refuses on, or the two disagree: a form that looks answerable
   * while the transport drops the answer is a question asked into a void.
   * V2 passes `transport.inFlight`.
   */
  formDisabled?: boolean;
};

export function AskPunttThread({
  messages,
  presentation = "chat",
  chatId,
  waitingForAI = false,
  feedActorName,
  feedActorAvatar,
  feedMembers,
  currentUserId,
  chatOwnerId,
  renderAfterMessage,
  renderBeforeMessage,
  hideAgentChromeForMessage,
  onSubmitAnswers,
  formDisabled = false,
}: AskPunttThreadProps) {
  const columnRef = useRef<HTMLDivElement>(null);
  const columnWidth = useContainerWidth(columnRef, presentation === "feed");
  const feedCompactAuthors =
    presentation === "feed" &&
    columnWidth != null &&
    columnWidth <= FEED_COMPACT_AUTHOR_MAX_WIDTH_PX;

  const visibleMessages = useMemo(
    () => messages.filter((message) => !isHiddenMessage(message)),
    [messages],
  );

  // why: an aborted transport row (hideAgentChromeForMessage === true) is a
  // terminal state, not a live pre-stream placeholder. Keep it visible even
  // when empty so its "Stopped" affordance (renderAfterMessage) survives —
  // otherwise feed always, and chat when not-last, drops the row silently.
  const isHiddenEmptyPlaceholder = (
    message: AskPunttMessage,
    isLast: boolean,
  ): boolean => {
    if (hideAgentChromeForMessage?.(message)) return false;
    return shouldHideEmptyAssistantPlaceholder({
      message,
      isLast,
      presentation,
    });
  };

  const lastVisibleMessageId = useMemo(() => {
    for (let i = visibleMessages.length - 1; i >= 0; i -= 1) {
      const message = visibleMessages[i];
      if (isHiddenEmptyPlaceholder(message, i === visibleMessages.length - 1)) {
        continue;
      }
      return message._id;
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentation, visibleMessages, hideAgentChromeForMessage]);

  const lastMsg = visibleMessages[visibleMessages.length - 1];
  const lastIsAssistant = lastMsg?.role === "assistant";
  const showFeedPlaceholderThinking =
    presentation === "feed" &&
    waitingForAI &&
    lastIsAssistant &&
    !!lastMsg &&
    isEmptyAssistantPlaceholder(lastMsg);

  const showThinking =
    waitingForAI && (!lastIsAssistant || showFeedPlaceholderThinking);

  const now = useMemo(() => new Date(), [visibleMessages.length]);

  const rows: ReactNode[] = [];
  let prevDay: string | null = null;
  let prevVisibleFeedAuthorKey: string | null = null;

  visibleMessages.forEach((message, idx) => {
    const isLast = idx === visibleMessages.length - 1;
    if (isHiddenEmptyPlaceholder(message, isLast)) {
      return;
    }

    const day = calendarDayKey(message.createdAt);
    if (day && day !== prevDay) {
      rows.push(
        <DayDivider
          key={`day-divider-${day}-${message._id}`}
          label={formatDayDivider(message.createdAt, now)}
          day={day}
          sticky={presentation === "feed"}
        />,
      );
      prevDay = day;
    }

    const feedActor =
      presentation === "feed" && message.role === "user"
        ? resolveFeedActorForUserMessage(message, {
            currentUserId,
            chatOwnerId,
            members: feedMembers,
            viewerName: feedActorName,
            viewerAvatar: feedActorAvatar,
          })
        : null;

    const feedAuthorKey =
      presentation === "feed" ? feedMessageAuthorKey(message) : null;
    const showFeedAuthorHeader =
      feedAuthorKey == null ||
      !feedCompactAuthors ||
      isFeedAuthorGroupStart(prevVisibleFeedAuthorKey, feedAuthorKey);
    if (feedAuthorKey != null) {
      prevVisibleFeedAuthorKey = feedAuthorKey;
    }

    const isStreaming = waitingForAI && isLast && message.role === "assistant";
    const isUserMessage = message.role === "user";

    // Parity with V1 chat.tsx: while the last assistant turn streams inline, its
    // ThinkingState must reflect the preceding USER turn's attachments so the
    // pre-agent read pause shows "Reading attachments..." instead of the generic
    // "Starting up...". Without forwarding this, the shared thread always lost
    // that phase in chat presentation.
    const inlinePrecedingUserHasAttachments =
      isStreaming && !!visibleMessages[idx - 1]?.attachments?.length;

    rows.push(
      // why: `useReadingMessage` finds the answer the reader is on by these,
      // so the v2 rail can show that answer's citations.
      <div
        key={message._id}
        data-message-id={message._id}
        data-message-role={message.role}
      >
        <ChatMessage
          message={message}
          chatId={chatId}
          isStreaming={isStreaming}
          precedingUserHasAttachments={inlinePrecedingUserHasAttachments}
          hideAgentChrome={hideAgentChromeForMessage?.(message) ?? false}
          // Answerable only while the transcript ends on this turn: once the
          // person replies, their words were the answer and the form locks.
          isLastAssistant={
            onSubmitAnswers ? isLast && !isUserMessage : undefined
          }
          onSubmitAnswers={onSubmitAnswers}
          formDisabled={formDisabled}
          presentation={presentation}
          feedActorName={feedActor ? feedActor.name : feedActorName}
          feedActorAvatar={feedActor ? feedActor.avatar : feedActorAvatar}
          feedOwnUserMessage={
            presentation === "feed" &&
            message.role === "user" &&
            isFeedMessageFromViewer(message, {
              currentUserId,
              chatOwnerId,
            })
          }
          feedMembers={feedMembers}
          collapsible={
            presentation === "feed" && message._id !== lastVisibleMessageId
          }
          feedCompactLayout={feedCompactAuthors}
          showAuthorHeader={showFeedAuthorHeader}
          renderBeforeContent={
            isUserMessage ? renderBeforeMessage?.(message) : undefined
          }
          renderAfterContent={
            isUserMessage ? renderAfterMessage?.(message) : undefined
          }
        />
        {!isUserMessage ? renderAfterMessage?.(message) : null}
      </div>,
    );
  });

  // why: in feed placeholder mode the last visible row is the empty assistant
  // shell, so its attachments are always empty. The user turn whose files the
  // agent is still reading sits one row back — read that, else the "Reading
  // attachments..." phase never fires and the pre-read pause looks silent.
  const precedingUserMessage = showFeedPlaceholderThinking
    ? visibleMessages[visibleMessages.length - 2]
    : visibleMessages[visibleMessages.length - 1];

  const thinkingState = (
    <ThinkingState
      message={showFeedPlaceholderThinking ? lastMsg : undefined}
      precedingUserHasAttachments={!!precedingUserMessage?.attachments?.length}
    />
  );
  const thinkingRow =
    presentation === "feed" && !feedCompactAuthors ? (
      <div className="mb-4 flex gap-3" data-testid="feed-thread-thinking-row">
        <div
          className="shrink-0 pt-0.5"
          data-testid="feed-thread-thinking-avatar-column"
        >
          <PunttAvatar size={AVATAR_SIZE_BIG} />
        </div>
        <div className="min-w-0 flex-1">{thinkingState}</div>
      </div>
    ) : (
      thinkingState
    );

  return (
    <FeedCompactLayoutProvider value={feedCompactAuthors}>
      <div ref={columnRef} className="w-full">
        {rows}
        {showThinking ? thinkingRow : null}
      </div>
    </FeedCompactLayoutProvider>
  );
}
